// src/utils/authHelper.ts
export interface TokenPayload {
    id?: string | number;
    sub?: string | number;
    email?: string;
    username?: string;
    exp?: number;
    iat?: number;
    [key: string]: any;
}

/**
 * Lấy User ID từ JWT token trong localStorage
 * @returns {string} User ID
 * @throws {Error} Nếu không có token hoặc token không hợp lệ
 */
export const getUserIdFromToken = (): string => {
    const token = localStorage.getItem('token');

    if (!token) {
        throw new Error('User not authenticated - no token found');
    }

    try {
        // JWT token có format: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid token format - not a valid JWT');
        }

        // Decode payload (phần thứ 2)
        const payload: TokenPayload = JSON.parse(atob(parts[1]));

        // Lấy user ID (có thể là 'id' hoặc 'sub' tùy vào cách implement)
        const userId = payload.id?.toString() || payload.sub?.toString();

        if (!userId) {
            throw new Error('User ID not found in token payload');
        }

        console.log('🔑 Retrieved User ID from token:', userId);
        return userId;
    } catch (error) {
        if (error instanceof Error) {
            console.error('❌ Error decoding token:', error.message);
            throw error;
        } else {
            console.error('❌ Unknown error decoding token:', error);
            throw new Error('Failed to decode JWT token');
        }
    }
};

/**
 * Lấy toàn bộ payload từ JWT token
 * @returns {TokenPayload} Token payload
 * @throws {Error} Nếu không có token hoặc token không hợp lệ
 */
export const getTokenPayload = (): TokenPayload => {
    const token = localStorage.getItem('token');

    if (!token) {
        throw new Error('User not authenticated - no token found');
    }

    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid token format - not a valid JWT');
        }

        const payload: TokenPayload = JSON.parse(atob(parts[1]));
        return payload;
    } catch (error) {
        console.error('❌ Error decoding token payload:', error);
        throw new Error('Failed to decode JWT token payload');
    }
};

/**
 * Kiểm tra token có hết hạn không
 * @returns {boolean} true nếu token còn hợp lệ, false nếu hết hạn
 */
export const isTokenValid = (): boolean => {
    try {
        const payload = getTokenPayload();

        if (!payload.exp) {
            // Nếu không có expiry, coi như token không hợp lệ
            return false;
        }

        // exp trong JWT token là timestamp tính bằng giây, Date.now() tính bằng milliseconds
        const expiry = payload.exp * 1000;
        const isValid = Date.now() < expiry;

        if (!isValid) {
            console.warn('⚠️ Token has expired');
        }

        return isValid;
    } catch (error) {
        console.error('❌ Error validating token:', error);
        return false;
    }
};

/**
 * Kiểm tra user có được authenticate không
 * @returns {boolean} true nếu có token hợp lệ
 */
export const isAuthenticated = (): boolean => {
    const token = localStorage.getItem('token');
    return !!token && isTokenValid();
};

/**
 * Lấy thông tin user cơ bản từ token
 * @returns {Object} Thông tin user từ token
 */
export const getUserInfoFromToken = (): {
    id: string;
    email?: string;
    username?: string;
} => {
    try {
        const payload = getTokenPayload();
        const userId = getUserIdFromToken();

        return {
            id: userId,
            email: payload.email,
            username: payload.username
        };
    } catch (error) {
        console.error('❌ Error getting user info from token:', error);
        throw error;
    }
};

/**
 * Format user ID để đảm bảo consistency
 * @param {string | number} userId - User ID cần format
 * @returns {string} Formatted user ID
 */
export const formatUserId = (userId: string | number): string => {
    if (typeof userId === 'number') {
        return userId.toString();
    }
    return userId?.toString() || '';
};

/**
 * Debug helper - log token information
 * Chỉ sử dụng trong development
 */
export const debugToken = (): void => {
    if (import.meta.env.NODE_ENV !== 'development') {
        return;
    }

    try {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('🔍 DEBUG: No token found');
            return;
        }

        const payload = getTokenPayload();
        const userInfo = getUserInfoFromToken();
        const isValid = isTokenValid();

        console.group('🔍 TOKEN DEBUG INFO');
        console.log('Token exists:', !!token);
        console.log('Token valid:', isValid);
        console.log('User ID:', userInfo.id);
        console.log('Email:', userInfo.email);
        console.log('Username:', userInfo.username);
        console.log('Token expires at:', payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A');
        console.log('Full payload:', payload);
        console.groupEnd();
    } catch (error) {
        console.error('🔍 DEBUG: Error inspecting token:', error);
    }
};

export const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
};

export const getClientInfo = () => {
    return {
        ipAddress: '127.0.0.1', // Will be set by backend
        userAgent: navigator.userAgent,
        deviceType: getDeviceType(),
        sessionId: getSessionId()
    };
};

const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' | 'unknown' => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/tablet|ipad|playbook|silk/.test(userAgent)) {
        return 'tablet';
    }

    if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/.test(userAgent)) {
        return 'mobile';
    }

    if (/desktop/.test(userAgent)) {
        return 'desktop';
    }

    return 'unknown';
};

const getSessionId = (): string => {
    let sessionId = sessionStorage.getItem('vietrro_session_id');
    if (!sessionId) {
        sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('vietrro_session_id', sessionId);
    }
    return sessionId;
};

/**
 * Get current user ID for content interactions
 * Uses existing getUserIdFromToken function
 */
export const getCurrentUserId = (): string | null => {
    try {
        return getUserIdFromToken();
    } catch (error) {
        console.warn('Could not get current user ID:', error);
        return null;
    }
};

/**
 * Check if user can interact with content
 * Combines authentication check with user ID validation
 */
export const canUserInteract = (): boolean => {
    try {
        return isAuthenticated() && !!getCurrentUserId();
    } catch (error) {
        return false;
    }
};

/**
 * Get user context for content interactions
 * Combines user info with client info
 */
export const getUserContext = () => {
    try {
        const userInfo = getUserInfoFromToken();
        const clientInfo = getClientInfo();

        return {
            ...userInfo,
            ...clientInfo,
            isAuthenticated: true
        };
    } catch (error) {
        return {
            id: null,
            email: null,
            username: null,
            isAuthenticated: false,
            ...getClientInfo()
        };
    }
};

export const getUserRole = (): string => {
    return localStorage.getItem('userRole') || 'guest';
};

export const isAdminRole = (role?: string): boolean => {
    const userRole = role || getUserRole();
    return ['admin', 'staff', 'legal'].includes(userRole);
};

export const canViewAllCertificates = (): boolean => {
    return isAdminRole();
};
